// TEMPLATES.md REMINDER
//
// If you modify public/templates/HigherGroundProgressReport_Template.xlsx,
// you MUST update TEMPLATES.md at the repo root with a versioned changelog
// entry describing what changed. The .xlsx produces an unreadable binary diff
// in GitHub — TEMPLATES.md is the only human-readable record of changes.
//
// Save the current file as HigherGroundProgressReport_Template_old.xlsx before modifying it
// then use it to compare against the new version HigherGroundProgressReport_Template.xlsx
// Use the custom prompt .github/prompts/update-templates-changelog.prompt.md
// to automate the diff analysis and TEMPLATES.md update.

import { HGReportResponse } from '../models/HGReportModel';

// ─── Sheet & layout constants ────────────────────────────────────────────────

const DATA_SHEET_NAME = 'Data';

// BINARY TEMPLATE — READ BEFORE MODIFYING
//
// The .xlsx template is a binary asset — PRs will show "binary file changed"
// without a readable diff. This code uses JSZip to replace ONLY the Data sheet
// XML inside the archive; all other files (report sheet, styles, formulas,
// conditional formatting) pass through untouched.
//
// HOW TO UPDATE THE TEMPLATE:
//   1. Open the .xlsx in Excel and make your changes.
//   2. Ensure the "Data" sheet layout matches the constants below
//      (rows 1–5 scalars, row 7 header, row 8+ metrics).
//   3. Save to public/templates/HigherGroundProgressReport_Template.xlsx and commit.
//   4. Update TEMPLATES.md at the repo root with a new versioned entry describing
//      what changed (use .github/prompts/update-templates-changelog.prompt.md).
//   5. Verify the "Progress Report" download works before merging.
const TEMPLATE_PATH = '/templates/HigherGroundProgressReport_Template.xlsx';

// Path inside the xlsx zip archive for the Data sheet
const DATA_SHEET_XML_PATH = 'xl/worksheets/sheet2.xml';

// Removed on export so Excel rebuilds the calculation chain from scratch
const CALC_CHAIN_XML_PATH = 'xl/calcChain.xml';

// Section 1 — scalar header rows (label in col A, value in col B)
const ROW_STORE_NUMBER = 1;
const ROW_STORE_NAME = 2;
const ROW_CURRENT_RANK_CODE = 3;
const ROW_PRIOR_RANK_CODE = 4;
const ROW_RANK_DATE = 5;

// Section 2 — metrics table
const ROW_METRICS_HEADER = 7;
const ROW_METRICS_START = 8;

// ─── Labels ──────────────────────────────────────────────────────────────────

const LABEL_STORE_NUMBER = 'StoreNumber';
const LABEL_STORE_NAME = 'StoreName';
const LABEL_CURRENT_RANK_CODE = 'CurrentRankCode';
const LABEL_PRIOR_RANK_CODE = 'PriorRankCode';
const LABEL_RANK_DATE = 'RankDate';
const LABEL_METRIC_CATEGORY = 'MetricCategory';
const LABEL_REPORT_TEXT = 'ReportText';
const LABEL_HITSTICK_STATUS = 'HitstickStatus';

// Metrics whose category starts with this prefix are written last in the Data sheet
const MRGP_ACTION_PREFIX = 'MRGP_Action_';

// ─── XML helpers ─────────────────────────────────────────────────────────────

const escapeXml = (value: string): string =>
  value.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;');

const COL_LETTERS = ['A', 'B', 'C', 'D', 'E'];

const buildCell = (col: number, row: number, value: string): string => {
  const ref = `${COL_LETTERS[col - 1]}${row}`;
  return `<c r="${ref}" t="inlineStr"><is><t>${escapeXml(value)}</t></is></c>`;
};

const buildRow = (rowNum: number, cells: string): string => `<row r="${rowNum}">${cells}</row>`;

// ─── Utility ─────────────────────────────────────────────────────────────────

const formatRankDateForFilename = (rankDate: string | null): string => {
  if (rankDate) {
    // rankDate format: "January - 2026" → "January-2026"
    return rankDate.replace(' - ', '-').trim();
  }
  return 'Unknown';
};

// ─── Data sheet XML builder ──────────────────────────────────────────────────

const buildDataSheetXml = (
  reportData: HGReportResponse,
  storeNumber: string,
  storeName: string,
): string => {
  const rows: string[] = [];

  // Section 1 — scalar fields (label in col A, value in col B)
  rows.push(
    buildRow(
      ROW_STORE_NUMBER,
      buildCell(1, ROW_STORE_NUMBER, LABEL_STORE_NUMBER) +
        buildCell(2, ROW_STORE_NUMBER, storeNumber),
    ),
  );
  rows.push(
    buildRow(
      ROW_STORE_NAME,
      buildCell(1, ROW_STORE_NAME, LABEL_STORE_NAME) + buildCell(2, ROW_STORE_NAME, storeName),
    ),
  );
  rows.push(
    buildRow(
      ROW_CURRENT_RANK_CODE,
      buildCell(1, ROW_CURRENT_RANK_CODE, LABEL_CURRENT_RANK_CODE) +
        buildCell(2, ROW_CURRENT_RANK_CODE, reportData.currentRankCode ?? ''),
    ),
  );
  rows.push(
    buildRow(
      ROW_PRIOR_RANK_CODE,
      buildCell(1, ROW_PRIOR_RANK_CODE, LABEL_PRIOR_RANK_CODE) +
        buildCell(2, ROW_PRIOR_RANK_CODE, reportData.priorRankCode ?? ''),
    ),
  );
  rows.push(
    buildRow(
      ROW_RANK_DATE,
      buildCell(1, ROW_RANK_DATE, LABEL_RANK_DATE) +
        buildCell(2, ROW_RANK_DATE, reportData.rankDate ?? ''),
    ),
  );

  // Section 2 — metrics table header
  rows.push(
    buildRow(
      ROW_METRICS_HEADER,
      buildCell(1, ROW_METRICS_HEADER, LABEL_METRIC_CATEGORY) +
        buildCell(2, ROW_METRICS_HEADER, LABEL_REPORT_TEXT) +
        buildCell(3, ROW_METRICS_HEADER, LABEL_HITSTICK_STATUS),
    ),
  );

  // Section 2 — metric data rows (MRGP_Action_ metrics written last)
  const standardMetrics = reportData.metrics.filter(
    m => !m.metricCategory.startsWith(MRGP_ACTION_PREFIX),
  );
  const actionMetrics = reportData.metrics.filter(m =>
    m.metricCategory.startsWith(MRGP_ACTION_PREFIX),
  );
  const orderedMetrics = [...standardMetrics, ...actionMetrics];

  orderedMetrics.forEach((metric, index) => {
    const rowNum = ROW_METRICS_START + index;
    rows.push(
      buildRow(
        rowNum,
        buildCell(1, rowNum, metric.metricCategory) +
          buildCell(2, rowNum, metric.reportText ?? '') +
          buildCell(3, rowNum, metric.hitstickStatus ?? ''),
      ),
    );
  });

  // Compute the dimension ref (e.g. "A1:C28")
  const lastRow = ROW_METRICS_START + orderedMetrics.length - 1;
  const maxCol = orderedMetrics.length > 0 ? 'C' : 'B';
  const dimensionRef = `A1:${maxCol}${lastRow}`;

  return [
    '<?xml version="1.0" encoding="UTF-8" standalone="yes"?>',
    '<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main"',
    '  xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">',
    `<dimension ref="${dimensionRef}"/>`,
    '<sheetData>',
    ...rows,
    '</sheetData>',
    '</worksheet>',
  ].join('');
};

// ─── Main export function ─────────────────────────────────────────────────────

export const generateHGProgressReport = async (
  reportData: HGReportResponse,
  storeNumber: string,
  storeName: string,
): Promise<void> => {
  const JSZip = (await import('jszip')).default;

  // Fetch the template from the public folder
  const response = await fetch(TEMPLATE_PATH);
  if (!response.ok) {
    throw new Error(`Failed to fetch Excel template: ${response.status} ${response.statusText}`);
  }
  const buffer = await response.arrayBuffer();

  // Open the template as a zip archive
  const zip = await JSZip.loadAsync(buffer);

  // Replace only the Data sheet XML — all other files pass through untouched
  const dataSheetXml = buildDataSheetXml(reportData, storeNumber, storeName);
  zip.file(DATA_SHEET_XML_PATH, dataSheetXml);
  zip.remove(CALC_CHAIN_XML_PATH);

  // Patch workbook.xml: force full recalc, hide the Data tab, fix active tab
  const workbookXmlFile = zip.file('xl/workbook.xml');
  if (workbookXmlFile) {
    let workbookXml = await workbookXmlFile.async('string');

    // fullCalcOnLoad forces Excel to recalculate all formulas on open
    if (workbookXml.includes('<calcPr')) {
      workbookXml = workbookXml.replace(/<calcPr/, '<calcPr fullCalcOnLoad="1"');
    } else {
      workbookXml = workbookXml.replace('</workbook>', '<calcPr fullCalcOnLoad="1"/></workbook>');
    }

    // veryHidden prevents users from unhiding the Data sheet via Excel UI
    workbookXml = workbookXml.replace(
      new RegExp(`(<sheet\\s[^>]*name="${DATA_SHEET_NAME}"[^/]*)(\\/\\s*>)`),
      '$1 state="veryHidden"$2',
    );

    // activeTab must point to a visible sheet or Excel rejects the file
    workbookXml = workbookXml.replace(/activeTab="[^"]*"/, 'activeTab="0"');

    zip.file('xl/workbook.xml', workbookXml);
  }

  // Generate the output xlsx
  const outputBlob = await zip.generateAsync({
    type: 'blob',
    mimeType: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
    compression: 'DEFLATE',
  });

  // Trigger browser download
  const filename = `Store-${storeNumber}-HigherGroundProgressReport-${formatRankDateForFilename(reportData.rankDate)}.xlsx`;
  const url = URL.createObjectURL(outputBlob);
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
  URL.revokeObjectURL(url);
};
